/**
 * Haptics.
 *
 * Android only, via `navigator.vibrate`. iOS Safari has no vibration API — research
 * confirmed it is still absent as of iOS 26.5 — and there is no fallback hack worth
 * shipping, so on iPhone this is simply silent. A Capacitor Taptic wrapper is the
 * post-V1 route, not a workaround to attempt here.
 *
 * Patterns are short and few. A controller that buzzes at everything stops meaning
 * anything, so only three events qualify: firing, a heavy launch, and taking a hit.
 */

const PATTERNS = {
  /** A burst of gunfire — light, and only on the leading round of a burst. */
  burst: [12],
  /** Torpedo away, or a depth charge leaving the projector. */
  launch: [45],
  /** The rig taking damage, and the strongest thing here. */
  impact: [30, 40, 60],
  /** An underwater detonation. Plan D3 calls this the strongest pattern in the game. */
  detonation: [24, 30, 24, 30, 70],
} as const;

export type HapticEvent = keyof typeof PATTERNS;

export class Haptics {
  enabled = true;
  private readonly supported: boolean;
  /** Rate limit: sustained fire must not become a continuous buzz. */
  private nextAllowedAt = 0;

  constructor() {
    this.supported = typeof navigator !== 'undefined'
      && typeof navigator.vibrate === 'function';
  }

  get available(): boolean {
    return this.supported;
  }

  play(event: HapticEvent, now: number): void {
    if (!this.enabled || !this.supported) return;
    // 120 ms between pulses. Below that the motor is still moving from the last one and
    // the result is mush rather than distinct events.
    if (now < this.nextAllowedAt) return;
    this.nextAllowedAt = now + 0.12;
    try {
      navigator.vibrate(PATTERNS[event] as unknown as number[]);
    } catch {
      // Some browsers throw when the page is not visible. Nothing to recover.
    }
  }
}
